import { HelpCircle } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

interface HelpTooltipProps {
  content: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  maxWidth?: string
}

export default function HelpTooltip({ 
  content, 
  position = 'top',
  maxWidth = '400px'
}: HelpTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(position)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Reset actual position when visibility or requested position changes
  useEffect(() => {
    setActualPosition(position)
  }, [position, isVisible])

  // Calculate optimal position based on viewport boundaries
  useEffect(() => {
    if (!isVisible || !tooltipRef.current || !buttonRef.current) return

    const calculateOptimalPosition = () => {
      const tooltip = tooltipRef.current
      const button = buttonRef.current
      if (!tooltip || !button) return

      // Wait for tooltip to be rendered and measured
      const tooltipRect = tooltip.getBoundingClientRect()
      const buttonRect = button.getBoundingClientRect()
      
      // Skip if tooltip hasn't been rendered yet (width/height are 0)
      if (tooltipRect.width === 0 || tooltipRect.height === 0) {
        return
      }

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const padding = 16 // Minimum padding from viewport edge

      let newPosition = position

      // Check horizontal overflow (for left/right positions)
      if (position === 'right') {
        const rightEdge = tooltipRect.right
        if (rightEdge > viewportWidth - padding) {
          // Would overflow on right, switch to left
          newPosition = 'left'
        }
      } else if (position === 'left') {
        const leftEdge = tooltipRect.left
        if (leftEdge < padding) {
          // Would overflow on left, switch to right
          newPosition = 'right'
        }
      }

      // Check vertical overflow (for top/bottom positions)
      if (position === 'bottom') {
        const bottomEdge = tooltipRect.bottom
        if (bottomEdge > viewportHeight - padding) {
          // Would overflow on bottom, switch to top
          newPosition = 'top'
        }
      } else if (position === 'top') {
        const topEdge = tooltipRect.top
        if (topEdge < padding) {
          // Would overflow on top, switch to bottom
          newPosition = 'bottom'
        }
      }

      // For horizontal positions, also check if there's enough space
      if (newPosition === 'right' || newPosition === 'left') {
        const spaceOnRight = viewportWidth - buttonRect.right
        const spaceOnLeft = buttonRect.left
        const tooltipWidth = tooltipRect.width

        if (newPosition === 'right' && spaceOnRight < tooltipWidth + padding && spaceOnLeft > spaceOnRight) {
          newPosition = 'left'
        } else if (newPosition === 'left' && spaceOnLeft < tooltipWidth + padding && spaceOnRight > spaceOnLeft) {
          newPosition = 'right'
        }
      }

      // For vertical positions, also check if there's enough space
      if (newPosition === 'top' || newPosition === 'bottom') {
        const spaceOnTop = buttonRect.top
        const spaceOnBottom = viewportHeight - buttonRect.bottom
        const tooltipHeight = tooltipRect.height

        if (newPosition === 'top' && spaceOnTop < tooltipHeight + padding && spaceOnBottom > spaceOnTop) {
          newPosition = 'bottom'
        } else if (newPosition === 'bottom' && spaceOnBottom < tooltipHeight + padding && spaceOnTop > spaceOnBottom) {
          newPosition = 'top'
        }
      }

      if (newPosition !== actualPosition) {
        setActualPosition(newPosition)
      }
    }

    // Use setTimeout to ensure DOM is fully updated and measured
    const timeoutId = setTimeout(() => {
      calculateOptimalPosition()
    }, 0)

    // Recalculate on window resize and scroll
    const handleResize = () => {
      calculateOptimalPosition()
    }
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize, true)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize, true)
    }
  }, [isVisible, position, actualPosition])

  useEffect(() => {
    if (!isVisible) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        buttonRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isVisible])

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  }

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-x-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-x-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 border-y-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 border-y-transparent border-l-transparent',
  }

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsVisible(!isVisible)
        }}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="inline-flex items-center justify-center w-5 h-5 ml-1 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full"
        aria-label="Help information"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 ${positionClasses[actualPosition]}`}
          style={{ 
            maxWidth,
            minWidth: '450px',
            width: 'fit-content'
          }}
          role="tooltip"
        >
          <div className="bg-gray-900 text-white text-xs rounded-lg px-4 py-3 shadow-lg" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="whitespace-pre-line leading-relaxed break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{content}</div>
            <div
              className={`absolute w-0 h-0 border-4 ${arrowClasses[actualPosition]}`}
              style={{ borderStyle: 'solid' }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
